/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import { emptySelectionModel, SelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { isEmpty, filter } from 'lodash';
import { Observable, Subject } from 'rxjs';

import {
  getQuoteVariationId,
  isQuoteVariationTotal,
  quoteTotalFromDerivedInfo,
  QuoteVariation,
  QuoteVariationTotal,
} from '../../../resources/quote.model';
import { QuoteDetailsCommandService } from '../quote-details-command.service';
import {
  queryQuoteDerivedInfo,
  queryQuoteIsProfitSupported,
  queryQuoteIsProfitVisible,
  queryQuoteVariationFocusedCell,
  queryQuoteVariations, queryQuoteVariationsFilter,
  QuoteDetailsState,
  QuoteDetailsStateEventType,
  VariationsFilter,
} from '../quote-details.state';
import { QuoteDetailsVariationGridDefinitionService } from './quote-details-variation-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-quote-details-variation-list',
  templateUrl: 'quote-details-variation-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QuoteDetailsVariationGridDefinitionService],
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class QuoteDetailsVariationListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  variations$: Observable<QuoteVariation[]>;
  filters$: Observable<VariationsFilter>;
  commonVariations: QuoteVariation[];
  total$: Observable<QuoteVariationTotal[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  supportsProfit$: Observable<boolean>;
  isProfitVisible$: Observable<boolean>;
  editable$: Observable<boolean>;
  quoteVarSelectionModel: SelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private changeDetectorRef: ChangeDetectorRef,
              private state: State<QuoteDetailsState>,
              private commands: QuoteDetailsCommandService,
              private dialogService: DialogService,
              public variationGridDefinition: QuoteDetailsVariationGridDefinitionService) {

  }

  ngOnInit(): void {
    const { variationGridLayout } = getRouteInitData(this.route);

    this.variationGridDefinition.bind(variationGridLayout);

    this.supportsProfit$ = this.state.select(queryQuoteIsProfitSupported);
    this.isProfitVisible$ = this.state.select(queryQuoteIsProfitVisible);

    this.filters$ = this.state.select(queryQuoteVariationsFilter);
    this.variations$ = combineLatest$(this.filters$, this.state.select(queryQuoteVariations)).pipe(
      map$(([ {search}, variations ]) => {
        if (!search) {
          return variations;
        } else {
          return filter(variations, (variation) =>
            variation.description.toLowerCase().includes(search.toLowerCase()) ||
            variation.itemCode.toLowerCase().includes(search.toLowerCase()));
        }
      }));
    this.total$ = combineLatest$(this.isProfitVisible$, this.state.select(queryQuoteDerivedInfo)).pipe(
      map$(([isProfitVisible, info]) => [quoteTotalFromDerivedInfo(info, isProfitVisible)]));
    this.focusedCell$ = this.state.select(queryQuoteVariationFocusedCell);

    this.editable$ = this.state.select(queryFormEditable);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClone(): void {
    this.commands.cloneVariations();
  }

  onAdd(): void {
    this.commands.addVariations();
  }

  onMoveCreditDebit(variation: QuoteVariation): void {
    this.commands.moveVariationCreditDebit(getQuoteVariationId(variation));
  }

  onViewMemo(variation: QuoteVariation): void {
    this.commands.viewVariationMemo(getQuoteVariationId(variation));
  }

  onRemove(variation: QuoteVariation): void {
    const id = getQuoteVariationId(variation);
    this.commands.removeVariation(id).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const rows = (this.state.project(queryQuoteVariations) || []).filter(
        (v) => !isQuoteVariationTotal(v),
      );
      const next = selectionModelAfterRowRemoved(
        this.quoteVarSelectionModel,
        id,
        rows.length === 0,
      );
      if (next !== this.quoteVarSelectionModel) {
        this.onQuoteVarSelectionChanged(next);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onCloneQuote(variation: QuoteVariation): void {
    this.commands.cloneQuoteVariation(getQuoteVariationId(variation));
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onQuoteVarSelectionChanged(model: SelectionModel): void {
    this.quoteVarSelectionModel = model;
  }

  getSelectedVariations(): Observable<QuoteVariation[]> {
    return this.variations$.pipe(
      map$((variations) => {
        if (!variations || variations.length === 0) {
          return [];
        }

        if (this.quoteVarSelectionModel.isExcludeMode) {
          return variations.filter((variation) => {
            const id = getQuoteVariationId(variation);
            return !isQuoteVariationTotal(variation) && !this.quoteVarSelectionModel.idList.includes(id);
          });
        } else {
          return variations.filter((variation) => {
            const id = getQuoteVariationId(variation);
            return !isQuoteVariationTotal(variation) && this.quoteVarSelectionModel.idList.includes(id);
          });
        }
      })
    );
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedVariations().pipe(
      map$((selectedVariations) => selectedVariations.length > 0)
    );
  }

  onDeleteSelected(): void {
    this.getSelectedVariations().pipe(
      take$(1),
      filter$((selectedVariations) => selectedVariations.length > 0),
      switchMap$((selectedVariations) => {
        const count = selectedVariations.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedVariations[0].itemCode })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedVariations));
      }),
    ).subscribe((selectedVariations) => {
      selectedVariations.forEach((variation) => {
        this.state.mutate({
          type: QuoteDetailsStateEventType.VariationRemove,
          variationId: getQuoteVariationId(variation),
        });
      });
      this.quoteVarSelectionModel = emptySelectionModel();
      this.onQuoteVarSelectionChanged(this.quoteVarSelectionModel);
    });
  }

  canMoveToDebit(variation: QuoteVariation): boolean {
    return variation.isCredit && variation.itemPrice > 0;
  }

  canMoveToCredit({ isCredit, origQuantity, quantity, itemPrice }: QuoteVariation): boolean {
    return !isCredit && origQuantity > 0 && quantity <= origQuantity && itemPrice > 0;
  }

  hasMemo(variation: QuoteVariation): boolean {
    return !isEmpty(variation.memo);
  }

  isVariation(variation: QuoteVariation): boolean {
    return !isQuoteVariationTotal(variation);
  }

  onSearch(event: string): void {
    this.commands.setVariationListFilter({
       search: event,
     });
  }
}
